import { defineStore } from "pinia";
import { fetchScopes } from "@/auth/scopes";
import type { HeldScopes } from "@/nav/navScopes";

/**
 * 🔴 **WHAT THIS SESSION MAY DO WHERE IT IS STANDING** (D54) — the shell's fourth piece of context,
 * beside the session, the production and the organisation.
 *
 * It exists for exactly one job: to stop the sidebar offering doors the gate will refuse. The
 * persona picker (D53) made that visible in one click — Rudi, a member, was shown **Admin**.
 *
 * 🔴 **Never authority.** Nothing may be permitted because of what is in here. Every request is
 * checked at the gate against the database as it stands at that moment (D41), so a role revoked a
 * second ago is already gone while this snapshot still says otherwise — which is fine, because the
 * snapshot only decides what is DRAWN. Read `navScopes.ts` before using it for anything else.
 */
interface ScopeState {
  access: string[];
  platform: string[];
  /** False until the server has answered. While false, nothing is hidden. */
  known: boolean;
  loading: boolean;
  /** The organisation the current answer is FOR — a switch makes the previous one wrong, not stale. */
  forOrganisation: string | null;
}

export interface ScopeDeps {
  read: () => Promise<HeldScopes>;
}

const defaultDeps: ScopeDeps = { read: () => fetchScopes() };
let deps: ScopeDeps = defaultDeps;

/**
 * 🔴 Which request is the CURRENT one. Module-level rather than state: it is bookkeeping about
 * requests in flight, not something a component should ever read or a devtool should show.
 */
let sequence = 0;

/** Test seam. */
export function setScopeDeps(overrides: Partial<ScopeDeps> | null): void {
  deps = overrides === null ? defaultDeps : { ...defaultDeps, ...overrides };
}

export const useScopeStore = defineStore("shell-scopes", {
  state: (): ScopeState => ({ access: [], platform: [], known: false, loading: false, forOrganisation: null }),
  getters: {
    held(state): HeldScopes {
      return { access: state.access, platform: state.platform, known: state.known };
    },
  },
  actions: {
    /**
     * Ask for the scopes this session holds in `organisationId`.
     *
     * 🔴 **ONLY THE LATEST REQUEST MAY WRITE** — a sequence number, not a `loading` flag.
     *
     * The first cut had `if (this.loading) return`, which does not defer a switch, it DROPS it: change
     * company while an answer is in flight and the rail keeps the previous company's doors, forever,
     * because nothing asks again. Its companion guard compared the arriving answer against the
     * organisation of the last COMPLETED load rather than the one now wanted, so it never fired on
     * the case it was written for. Both are the D48 switcher bug in different clothes, and both were
     * mine, in the file whose comment warns about it.
     *
     * A sequence handles the pair: every call is issued, and a superseded answer is discarded on
     * arrival however long it took.
     */
    async load(organisationId: string | null): Promise<void> {
      if (organisationId === null) {
        // No company chosen: no scopes anywhere, and nothing to hide on. Not a failure — a session
        // that has chosen nothing holds nothing, and `navScopes.ts` hides nothing on that basis.
        sequence += 1;
        this.$patch({ access: [], platform: [], known: false, loading: false, forOrganisation: null });
        return;
      }
      const mine = (sequence += 1);
      this.loading = true;
      const held = await deps.read();
      // A newer ask has been issued since: that one owns the answer, and this one says nothing —
      // including about `loading`, which the newer request is still legitimately holding.
      if (mine !== sequence) return;
      this.$patch({
        access: [...held.access],
        platform: [...held.platform],
        known: held.known,
        loading: false,
        forOrganisation: organisationId,
      });
    },

    /** Sign-out, or a switch: forget it rather than let the last company's doors linger. */
    clear(): void {
      // Bumped so an answer already in flight cannot land after the clear and re-open the rail.
      sequence += 1;
      this.$patch({ access: [], platform: [], known: false, loading: false, forOrganisation: null });
    },
  },
});
